/**
 * WindowGame.js
 * Implements the game context window
 */

import {
    Game,
    GameWindow,
    OptionsMenuMode,
    UiElement,
    UiText,
    UiImage,
    GameTween,
    GameAnimatedSprite,
    UiButton,
    UiFrame,
    UiNineSlice,
    GameTimer
} from "pixi_core";

import { EnemySideHud } from "../HUD/EnemySideHud";
import { GameHud } from "../HUD/GameHud";
import { GameManager } from "../game/GameManager";
import { HeroSideHud } from "../HUD/HeroSideHud";
import { Common } from "../game/Common";

import {
    GameEvents,
    PAUSE_PREFAB,
    MUSIC_FADE_OUT_TIME,
    MUSIC_FADE_IN_TIME,
    CLEAR_COLOR_INGAME,
    NB_MAX_COLUMN,
    CANNON_SWAP_ANIM_DELAY,
    TUTORIAL_ARROW_ROTATION,
    GEAR_MENU_Y_POS,
    GEAR_MENU_X_POS,
    CANNON_RESERVE_MIN_HEIGHT,
    SETTINGS_PREFAB
} from "../Constants";

export class WindowGame extends GameWindow {
    constructor() {
        super({
            name: "WindowGame",
            isGamePhase: true
        });
        this.setFullScreen();

        /** @private @type {UiElement} */
        this._ui = null;

        /** @private @type {EnemySideHud} */
        this._enemySideHud = null;

        /** @private @type {HeroSideHud} */
        this._heroSideHud = null;

        /** @private @type {GameHud} */
        this._gameHud = null;

        /** @private @type {UiText} */
        this._waveCounter = null;

        /** @private @type {UiImage} */
        this._paddingTop = null;

        /** @private @type {UiImage} */
        this._paddingBottom = null;

        /** @private @type {UiImage} */
        this._paddingLeft = null;

        /** @private @type {UiImage} */
        this._paddingRight = null;

        /** @private @type {UiImage} */
        this._warningLineRed = null;

        /** @private @type {UiButton} */
        this._btnSwap = null;

        /** @private @type {UiImage} */
        this._cannonHighlight = null;

        /** @private @type {UiNineSlice} */
        this._imgReserve = null;

        /** @private @type {GameAnimatedSprite} */
        this._fxSwap = null;

        /** @private @type {UiImage} */
        this._tutorialAimTouch = null;
        /** @private @type {UiFrame} */
        this._tutorialAimKeyboard = null;

        /** @private @type {GameTimer} */
        this._timerSwapAnimDelay = null;

        /** @private @type {GameTween} */
        this._warningLineRedTween = null;

        /** @private @type {GameTween} */
        this._tweenReserve = null;

        /** @private @type {GameManager} */
        this._gameManager = new GameManager();

        /** @private @type {Number} */
        this._currentLevel = -1;

        Game.addUpdateListener(this._onUpdate, this);

        Game.on(GameEvents.eGAME_SET_LEVEL, this._onSetLevel, this);
        Game.on(GameEvents.eGAME_NEXT_LEVEL, this._onNextLevel, this);
        Game.on(GameEvents.eGAME_SET_WARNING_LINE_OPACITY, this._onSetWarningLineOpacity, this);
        Game.on(GameEvents.eSWAP_HINT_BEGIN, this._beginSwapHint, this);
        Game.on(GameEvents.eSWAP_HINT_END, this._endSwapHint, this);
        Game.on(GameEvents.eCANNON_RESERVE_CHANGED, this._onCannonReserveChanged, this);

        Game.on(GameEvents.eTUTORIAL_HIGHLIGHT_CANNON, this._onHighlightCannon, this);
        Game.on(GameEvents.eTUTORIAL_HIGHLIGHT_AIM, this._onHighlightAim, this);
        Game.on(GameEvents.eTUTORIAL_HIGHLIGHT_CLEAR, this._onHighlightClear, this);
    }

    /**
     * Update Loop
     * @private
     * @param {Number} deltaTime
     */
    _onUpdate(deltaTime) {
        if (!this.isOpened || Game.isPaused()) return;

        if (this._enemySideHud) {
            this._enemySideHud.updateLoop(deltaTime);
        }
        if (this._heroSideHud) {
            this._heroSideHud.updateLoop(deltaTime);
        }

        this._gameManager.updateLoop(deltaTime);
    }

    /**
     * @private
     * @param {Number} levelNumber
     */
    _onSetLevel(levelNumber) {
        this._currentLevel = levelNumber;
    }

    /**
     * @private
     */
    _onNextLevel() {
        ++this._currentLevel;
    }

    /**
     * @private
     * @param {Number} act
     */
    _setPadding(act) {
        Game.setClearColor(CLEAR_COLOR_INGAME[act]);
        this._paddingTop.setSprite("BGD_Background0" + act.toString() + "_PaddingTop");
        this._paddingBottom.setSprite("BGD_Background0" + act.toString() + "_PaddingBottom");
        this._paddingLeft.setSprite("BGD_Background0" + act.toString() + "_PaddingLeft");
        this._paddingRight.setSprite("BGD_Background0" + act.toString() + "_PaddingRight");
    }

    /**
     * The window is opening for the first time
     * @protected
     */
    _onFirstWindowOpening() {
        this._ui = Game.ui.instantiate("Game", this);

        this._paddingTop = this._ui.findElementByName("ImgPaddingTop");
        this._paddingBottom = this._ui.findElementByName("ImgPaddingBottom");
        this._paddingLeft = this._ui.findElementByName("ImgPaddingLeft");
        this._paddingRight = this._ui.findElementByName("ImgPaddingRight");

        this._warningLineRed = this._ui.findElementByName("ImgWarningLineRed");

        /** @type {UiFrame} */
        const frmShooter = this._ui.findElementByName("Shooter");
        this._btnSwap = frmShooter.findElementByName("BtnSwap");
        this._cannonHighlight = frmShooter.findElementByName("Highlight");
        this._cannonHighlight.visible = false;

        this._imgReserve = frmShooter.findElementByName("ImgReserve");
        this._imgReserve.height = CANNON_RESERVE_MIN_HEIGHT;

        this._tutorialAimTouch = this._ui.findElementByName("TutorialAimTouch");
        this._tutorialAimTouch.visible = false;
        this._tutorialAimTouch.rotation = TUTORIAL_ARROW_ROTATION;

        this._tutorialAimKeyboard = this._ui.findElementByName("TutorialAimKeyboard");
        this._tutorialAimKeyboard.visible = false;

        const imgAim = this._tutorialAimKeyboard.findElementByName("Aim");
        imgAim.rotation = TUTORIAL_ARROW_ROTATION;
        const imgKeyDown = this._tutorialAimKeyboard.findElementByName("KeyDown");
        imgKeyDown.rotation = 180;

        this._gameHud = new GameHud(this._ui);
        this._enemySideHud = new EnemySideHud(this._ui);
        this._heroSideHud = new HeroSideHud(this._ui);
    }

    /**
     * The window is opening
     * @protected
     */
    _onWindowOpening() {
        // Options menu for this context
        Game.windows.optionsMenu.reset({
            prefab: PAUSE_PREFAB,
            prefabSettings: SETTINGS_PREFAB,
            mode: OptionsMenuMode.ePAUSE
        });

        // Configure the gear button for this context
        Game.windows.showGearButton({
            fullScreen: true,
            idSprite: "BTN_Gear",
            idSpriteOver: "BTN_GearHover",
            idSpritePressed: "BTN_GearPressed",
            idSpriteBack: "BTN_Back",
            idSpriteBackOver: "BTN_BackHover",
            idSpriteBackPressed: "BTN_BackPressed",
            scalePressed: 1,
            x: GEAR_MENU_X_POS,
            y: GEAR_MENU_Y_POS
        });

        this._enemySideHud.initialize();
        this._gameManager.createLevel(this._currentLevel);
        this._gameHud.setLevel(this._currentLevel);

        this._imgReserve.height = CANNON_RESERVE_MIN_HEIGHT;

        this._setPadding(this._gameManager.getAct());
        this._warningLineRed.setOpacity(0);
    }

    /**
     * The window is opened
     * @protected
     */
    _onWindowOpened() {
        this._gameManager.beginLevel();

        Game.audio.playMusic("IDSND_MUSIC_INGAME", { fadeOutTime: MUSIC_FADE_OUT_TIME, fadeInTime: MUSIC_FADE_IN_TIME });
    }

    /**
     * The window is closing
     * @protected
     */
    _onWindowClosing() {
        if (this._gameManager) {
            this._gameManager.closeLevel();
        }

        if (this._enemySideHud) {
            this._enemySideHud.closeLevel();
        }

        if (this._heroSideHud) {
            this._heroSideHud.closeLevel();
        }

        Common.tutorial.closeLevel();

        if (this._warningLineRedTween) {
            this._warningLineRedTween.destroy();
            this._warningLineRedTween = null;
        }

        if (this._tweenReserve) {
            this._tweenReserve.destroy();
            this._tweenReserve = null;
        }
    }

    /**
     * The window is closed
     * @protected
     */
    _onWindowClosed() {
        Game.audio.stopAllSfx();
        Game.audio.stopMusic("IDSND_MUSIC_INGAME");

        this._endSwapHint();
        this._onHighlightClear();
    }

    /**
     * @private
     */
    _beginSwapHint() {
        this._fxSwap = new GameAnimatedSprite(null, Game.design.getGraphicData("CannonArrow"));
        this._fxSwap.visible = false;
        this._btnSwap.uiRoot.addChild(this._fxSwap);

        this._fxSwap.onComplete = () => {
            if (this._fxSwap) {
                this._fxSwap.stop();
                this._fxSwap.visible = false;

                this._timerSwapAnimDelay.start();
            }
        };

        if (this._timerSwapAnimDelay) {
            this._timerSwapAnimDelay.destroy();
            this._timerSwapAnimDelay = null;
        }

        this._timerSwapAnimDelay = Game.windows
            .getCurrentWindow()
            .createTimer(CANNON_SWAP_ANIM_DELAY, { autoStart: false, deleteOnCompletion: false })
            .start();
        this._timerSwapAnimDelay.onComplete = () => {
            if (this._fxSwap) {
                this._fxSwap.visible = true;
                this._fxSwap.playAnimation("change", false);
            }
        };
    }

    /**
     * @private
     */
    _endSwapHint() {
        if (this._fxSwap && !this._fxSwap.destroyed) {
            this._fxSwap.destroy();
        }
        this._fxSwap = null;

        if (this._timerSwapAnimDelay) {
            this._timerSwapAnimDelay.destroy();
            this._timerSwapAnimDelay = null;
        }
    }

    /**
     * The window inputs have been enabled
     * @protected
     * @override
     */
    _onInputEnabled() {
        super._onInputEnabled();

        Game.fire(GameEvents.eRESET_BUTTON_INPUTS);
    }

    /**
     * The window inputs have been enabled
     * @private
     * @param {Number} nbColumns
     */
    _onSetWarningLineOpacity(nbColumns) {
        if (nbColumns >= NB_MAX_COLUMN - 2) {
            if (!this._warningLineRedTween) {
                this._warningLineRedTween = this.createTween({ value: 1 })
                    .to({ value: 0 }, Common.settings.redWarningLinePulse)
                    .onUpdate((object) => this._warningLineRed.setOpacity(object.value))
                    .repeat(Infinity)
                    .yoyo(true)
                    .start();
            }
        } else {
            if (this._warningLineRedTween) {
                this._warningLineRedTween.stop();
                this._warningLineRed.setOpacity(0);
                this._warningLineRedTween.destroy();
                this._warningLineRedTween = null;
            }
        }
    }

    /**
     * @private
     * @param {Number} nbBubblesInReserve
     */
    _onCannonReserveChanged(nbBubblesInReserve) {
        if (this._tweenReserve) {
            this._tweenReserve.destroy();
            this._tweenReserve = null;
        }

        const nbVisibleExtraBubbles = Math.min(nbBubblesInReserve - 1, Common.settings.cannonExtraVisibleBubbles);

        const targetHeight = CANNON_RESERVE_MIN_HEIGHT + nbVisibleExtraBubbles * Common.settings.cannonReserveSpacing;

        if (targetHeight === this._imgReserve.height) {
            return;
        }

        this._tweenReserve = this.createTween(this._imgReserve)
            .to({ height: targetHeight }, Common.settings.cannonReloadTime)
            .easing(GameTween.Easing.QuadraticOut)
            .onComplete(() => {
                if (this._tweenReserve) {
                    this._tweenReserve.destroy();
                    this._tweenReserve = null;
                }
            })
            .start();
    }

    /**
     * @private
     */
    _onHighlightCannon() {
        this._cannonHighlight.visible = true;
    }

    /**
     * @private
     */
    _onHighlightAim() {
        if (Common.tutorials.usingPointerControls) {
            if (this._tutorialAimTouch) {
                this._tutorialAimTouch.visible = true;
            }
        } else {
            if (this._tutorialAimKeyboard) {
                this._tutorialAimKeyboard.visible = true;
            }
        }
    }

    /**
     * @private
     */
    _onHighlightClear() {
        if (this._cannonHighlight) {
            this._cannonHighlight.visible = false;
        }

        if (this._tutorialAimTouch) {
            this._tutorialAimTouch.visible = false;
        }
        if (this._tutorialAimKeyboard) {
            this._tutorialAimKeyboard.visible = false;
        }
    }
}
